import {DestroyRef, effect, inject, Injectable, OnDestroy, signal} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {
    createLutTypedArray,
    DataResolver,
    lutSize,
    MeshRenderSettings,
    resolveTextureResolution,
    ThreeColormassMaterial,
    ThreeContext,
} from "@cm/material-nodes"
import {CachedEvaluator} from "@cm/graph-v3"
import type {CmLogger} from "@cm/log"
import {EXRLoader, Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {IMaterialData, keyForMeshMaterialData, TextureResolution, TextureStreamingMode} from "@cm/template-nodes"
import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"
import {catchError, debounceTime, defer, EMPTY, Observable, Subject, tap} from "rxjs"
import {DEFAULT_FLOAT_TEXTURE_TYPE, flipThreeSide} from "@app/template-editor/helpers/three-utils"
import {ThreeTextureCache} from "@app/template-editor/helpers/three-texture-cache"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {DataResolverService} from "@material-editor/services/data-resolver.service"
import {ThreeTextureTilesManagerService} from "@template-editor/services/three-texture-tiles-manager.service"
import {ThreeTiledTextureInstance} from "@cm/three-texture-streaming-browser"
import {
    DataObject,
    DataObjectWithRelated,
    extensionForContentType,
    fetchOk,
    getTypedArrayConstructorByDataType,
    isTypedImageData,
    TypedImageData,
} from "@cm/utils"
import {defaultMaterialParameters} from "@cm/render-nodes"
import {
    ImageDataObjectInfo,
    ImageDataObjectThumbnailTriggerService,
    isLegacyImageDataObject,
} from "@common/services/generate-thumbnail/image-data-object-thumbnail-trigger.service"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {decodeTiffToImageData} from "@common/helpers/utils/decode-tiff"

@Injectable()
export class ThreeMaterialManagerService implements OnDestroy {
    private threeSceneManagerService: ThreeSceneManagerService | undefined = undefined
    private asyncPromiseSerializerByMaterial = new Map<string, AsyncReentrancyGuard.PromiseSerializer>()
    private keyByThreeMaterial = new Map<THREE.Material, string>()
    private materialCache = new Map<
        string,
        {
            material: THREE.Material
            key: string
            refCount: number
            info: MaterialInfo
            variations: THREE.Material[]
        }
    >()
    private textureCache = new ThreeTextureCache()

    private dataResolver = inject(DataResolverService)
    private readonly imageDataObjectThumbnailTrigger = inject(ImageDataObjectThumbnailTriggerService)
    readonly textureTilesManager = inject(ThreeTextureTilesManagerService)
    readonly logger = inject(CM_LOGGER_MATERIALS)
    private readonly destroyRef = inject(DestroyRef)

    readonly $progressiveTextureLoading = signal(true)
    readonly $threeRenderer = signal<THREE.WebGPURenderer | undefined>(undefined)
    private requestedRedraw = new Subject<void>()
    private readonly legacyThumbnailAutoTrigger = new Subject<void>()
    private defaultTextureResolution: TextureResolution = "2000px"
    private defaultTextureStreamingMode: TextureStreamingMode = "high-quality"
    private maxTextureSize: number | undefined = undefined

    materialModifier: ((material: THREE.Material) => void) | undefined = undefined
    requestedRedraw$ = this.requestedRedraw.asObservable()

    constructor() {
        effect(() => {
            const threeRenderer = this.$threeRenderer()
            this.maxTextureSize = this.getMaxTextureSize(threeRenderer)
        })
        this.textureTilesManager.threeMaterialManagerService = this
        this.legacyThumbnailAutoTrigger.pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            void this.triggerUpdateJobsForImageDataObjects({legacyOnly: true, skipFailed: true})
        })
    }

    triggerUpdateJobsForImageDataObjects = (args: {legacyOnly: boolean; skipFailed: boolean}): Promise<number> => {
        const imageDataObjects = Array.from(this.materialCache.values())
            .filter((cachedMaterial) => cachedMaterial.info.textureStreamingMode !== "off")
            .flatMap((cachedMaterial) => cachedMaterial.info.imageDataObjects)
        return this.imageDataObjectThumbnailTrigger.triggerUpdateJobs(imageDataObjects, args)
    }

    setThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService) {
        this.threeSceneManagerService = threeSceneManagerService
        this.textureTilesManager.threeSceneManagerService = threeSceneManagerService
    }

    private tryToAcquireFromCache = (key: string) => {
        const cachedMaterial = this.materialCache.get(key)
        if (cachedMaterial !== undefined) {
            cachedMaterial.refCount++
            return cachedMaterial.material
        }

        return null
    }

    getInfoForMaterial = (key: string): MaterialInfo | null => {
        const cachedMaterial = this.materialCache.get(key)
        if (cachedMaterial !== undefined) {
            return cachedMaterial.info
        }
        return null
    }

    getKeyByThreeMaterial = (material: THREE.Material): string | null => {
        const key = this.keyByThreeMaterial.get(material)
        if (key) {
            return key
        }
        return null
    }

    acquireMaterial(
        materialData: IMaterialData,
        meshRenderSettings: MeshRenderSettings | undefined,
        materialIndex: number,
    ): [THREE.Material, Observable<THREE.Material> | null] {
        const key = keyForMeshMaterialData(materialData, meshRenderSettings)

        const cachedMaterial = this.tryToAcquireFromCache(key)
        if (cachedMaterial) return [cachedMaterial, null]

        const generateMaterial = async () => {
            let asyncPromiseSerializer = this.asyncPromiseSerializerByMaterial.get(key)
            if (asyncPromiseSerializer === undefined) {
                asyncPromiseSerializer = new AsyncReentrancyGuard.PromiseSerializer()
                this.asyncPromiseSerializerByMaterial.set(key, asyncPromiseSerializer)
            }

            return asyncPromiseSerializer.executeSequentially(async () => {
                const cachedMaterial = this.tryToAcquireFromCache(key)
                if (cachedMaterial) return cachedMaterial

                if (this.threeSceneManagerService === undefined) throw new Error("ThreeSceneManagerService is not set")

                const textureResolution = materialData.realtimeSettings?.textureResolution ?? this.defaultTextureResolution
                const textureStreamingMode = materialData.realtimeSettings?.textureStreamingMode ?? this.defaultTextureStreamingMode
                const {context, referencedTextureNodes, referencedTiledTextureInstances, getImageDataObjects} = this.createThreeContext({
                    textureResolution,
                    textureStreamingMode,
                    meshRenderSettings,
                    forceFiltering: materialData.realtimeSettings?.textureFiltering,
                })

                const material = await materialData.materialGraph.compileToThree(new CachedEvaluator<ThreeContext>(context))
                material.name = `ThreeMaterialManager(${key}) ${materialData.name}`

                this.setUpMaterial(material, materialData, key, {
                    isTransparent: material.transparent || (material.blending !== THREE.NormalBlending && material.blending !== THREE.NoBlending),
                    referencedTextureNodes,
                    referencedTiledTextureInstances,
                    imageDataObjects: getImageDataObjects(),
                    defaultMaterial: false,
                    textureStreamingMode,
                })

                return material
            })
        }

        return [this.acquireDefaultMaterial(materialData, materialIndex), materialData.realtimeSettings?.disable ? null : defer(() => generateMaterial())]
    }

    createThreeContext(options: {
        textureResolution: TextureResolution
        textureStreamingMode: TextureStreamingMode
        meshRenderSettings?: MeshRenderSettings
        forceFiltering?: "linear" | "nearest"
        threeRenderer?: THREE.WebGPURenderer
        progressiveTextureLoading?: boolean
        resolve?: DataResolver
        logger?: CmLogger
    }) {
        const {textureResolution, textureStreamingMode, meshRenderSettings, forceFiltering} = options
        const logger = options.logger ?? this.logger

        const referencedTextureNodes: (THREE.Texture | THREE.TextureNode | THREE.RenderTarget<THREE.Texture | THREE.Texture[]>)[] = []
        const referencedTiledTextureInstances: ThreeTiledTextureInstance[] = []
        const textureSetRevisionIdsByImageDataObjects = new Map<DataObjectWithRelated, Set<string>>()

        const vertexShaderAdditions = new Set<string>()
        const fragmentShaderAdditions = new Set<string>()
        let lutDataTexture: THREE.DataTexture | undefined = undefined
        const maxLuts = 32
        let lutIndex = 0
        const context: ThreeContext = {
            type: "three",
            textureResolution,
            meshRenderSettings,
            textureStreamingMode,
            threePushLutTexture: (lutRgba: number[][]) => {
                if (lutIndex >= maxLuts) throw new Error(`LUT index out of bounds, maximum is ${maxLuts - 1}, got ${lutIndex}`)
                const flatData = lutRgba.flat()
                if (flatData.length !== lutSize * 4) throw new Error(`LUT must be of size ${lutSize * 4}, got ${flatData.length}`)

                if (!lutDataTexture) {
                    const emptyData = createLutTypedArray(new Array(maxLuts * lutSize * 4).fill(0), DEFAULT_FLOAT_TEXTURE_TYPE)
                    lutDataTexture = new THREE.DataTexture(emptyData, lutSize, maxLuts, THREE.RGBAFormat, DEFAULT_FLOAT_TEXTURE_TYPE)
                    lutDataTexture.minFilter = THREE.LinearFilter
                    lutDataTexture.magFilter = THREE.LinearFilter
                    lutDataTexture.wrapS = THREE.ClampToEdgeWrapping
                    lutDataTexture.wrapT = THREE.ClampToEdgeWrapping
                    lutDataTexture.anisotropy = 1
                    lutDataTexture.colorSpace = THREE.LinearSRGBColorSpace
                    context.onThreeCreatedTexture?.(lutDataTexture)
                }

                if (!lutDataTexture.image.data) throw new Error("LUT data texture image data is not defined")

                lutDataTexture.image.data.set(createLutTypedArray(flatData, DEFAULT_FLOAT_TEXTURE_TYPE), lutIndex * lutSize * 4)
                lutDataTexture.needsUpdate = true

                return {
                    texture: lutDataTexture,
                    index: lutIndex++,
                }
            },
            registerImageDataObject: (resolvedDataObject: DataObjectWithRelated, textureSetRevisionId?: string) => {
                if (!resolvedDataObject.imageTilesDescriptor) {
                    logger.warn("Legacy image data object referenced", resolvedDataObject)
                }
                let textureSetRevisionIds = textureSetRevisionIdsByImageDataObjects.get(resolvedDataObject)
                if (textureSetRevisionIds === undefined) {
                    textureSetRevisionIds = new Set<string>()
                    textureSetRevisionIdsByImageDataObjects.set(resolvedDataObject, textureSetRevisionIds)
                }
                if (textureSetRevisionId !== undefined) {
                    textureSetRevisionIds.add(textureSetRevisionId)
                }
            },
            createImageTilesLookupNode: async (imageTilesDescriptor, isSrgb, uvNode) => {
                try {
                    const maxTextureResolution = resolveTextureResolution(textureResolution)
                    const tiledTextureInstance = await this.textureTilesManager.createTiledTexture(imageTilesDescriptor, isSrgb, uvNode, maxTextureResolution)
                    referencedTiledTextureInstances.push(tiledTextureInstance)
                    return tiledTextureInstance.textureNode
                } catch (error) {
                    logger.error("Failed to create image tiles lookup node. Using placeholder instead.", error)
                    return TSL.vec4(1, 0, 0, 1) as unknown as THREE.Node<"vec4">
                }
            },
            onThreeCreatedTexture: (textureNode) => {
                referencedTextureNodes.push(textureNode)
            },
            onThreeRequestShaderAdditions: (type, addition) => {
                if (type === "vertex") vertexShaderAdditions.add(addition)
                else if (type === "fragment") fragmentShaderAdditions.add(addition)
            },
            onRequestRedraw: () => {
                this.requestedRedraw.next()
            },
            progressiveTextureLoading: options.progressiveTextureLoading ?? this.$progressiveTextureLoading(),
            forceFiltering,
            threeRenderer: options.threeRenderer ?? this.$threeRenderer(),
            isTextureCached: (dataObject) => this.textureCache.hasForDownloadUrl(dataObject.downloadUrl),
            loadTexture: this.loadTexture.bind(this),
            releaseTexture: (texture: THREE.Texture) => this.releaseTextureResource(texture),
            threeDefaultFloatTextureType: DEFAULT_FLOAT_TEXTURE_TYPE,
            resolve: options.resolve ?? this.dataResolver.resolve,
            logger,
        }

        return {
            context,
            referencedTextureNodes,
            referencedTiledTextureInstances,
            getImageDataObjects: (): ImageDataObjectInfo[] =>
                [...textureSetRevisionIdsByImageDataObjects.entries()].map(([dataObject, revisionIds]) => ({
                    dataObjectWithRelated: dataObject,
                    associatedTextureSetRevisionIds: [...revisionIds],
                })),
        }
    }

    acquireDefaultMaterial(materialData: IMaterialData | undefined, materialIndex: number): THREE.Material {
        const usedMaterialData = materialData ?? {side: "front", alphaMaskThreshold: 0}
        const key = `default-${materialIndex},${usedMaterialData.side},${usedMaterialData.alphaMaskThreshold}`

        const cachedMaterial = this.tryToAcquireFromCache(key)
        if (cachedMaterial) return cachedMaterial

        const material = getDefaultMaterial(materialIndex)

        this.setUpMaterial(material, usedMaterialData, key, {
            isTransparent: false,
            referencedTextureNodes: [],
            referencedTiledTextureInstances: [],
            imageDataObjects: [],
            defaultMaterial: true,
            textureStreamingMode: "off",
        })
        return material
    }

    private setUpMaterial(
        material: THREE.Material,
        materialData: Pick<IMaterialData, "side" | "alphaMaskThreshold" | "realtimeSettings">,
        key: string,
        info: MaterialInfo,
    ) {
        switch (materialData.side) {
            case "front":
                material.side = THREE.FrontSide
                break
            case "back":
                material.side = THREE.BackSide
                break
            case "double":
                material.side = THREE.DoubleSide
                break
            default:
                material.side = THREE.FrontSide
                break
        }

        // Per-assignment opt-in: render alpha-driven transparency as hashed coverage (opaque queue, correct
        // depth under TAA) instead of blending. Transmission-driven transparency is unaffected.
        const isTransmission = material instanceof THREE.MeshPhysicalNodeMaterial && material.transmissionNode !== null
        if (materialData.realtimeSettings?.alphaHashing && material.transparent && !isTransmission) {
            material.transparent = false
            material.alphaHash = true
        }

        if (material.alphaHash) {
            material.shadowSide = flipThreeSide(material.side)
            material.side = THREE.DoubleSide
        } else {
            material.shadowSide = null
        }
        material.alphaTest = materialData.alphaMaskThreshold ?? 0
        this.materialModifier?.(material)

        this.keyByThreeMaterial.set(material, key)
        this.materialCache.set(key, {
            material,
            key,
            refCount: 1,
            info,
            variations: [],
        })

        if (!info.defaultMaterial && info.textureStreamingMode !== "off" && info.imageDataObjects.some(isLegacyImageDataObject)) {
            this.legacyThumbnailAutoTrigger.next()
        }
    }

    releaseMaterial(material: THREE.Material | THREE.Material[]) {
        if (Array.isArray(material)) for (const m of material) this.releaseMaterial(m)
        else {
            const cachedMaterial = [...this.materialCache.values()].find((x) => x.material === material || x.variations.includes(material))
            if (cachedMaterial !== undefined) {
                cachedMaterial.refCount--
                if (cachedMaterial.refCount <= 0) {
                    this.keyByThreeMaterial.delete(cachedMaterial.material)
                    for (const variation of cachedMaterial.variations) this.keyByThreeMaterial.delete(variation)
                    this.materialCache.delete(cachedMaterial.key)
                    for (const textureNode of cachedMaterial.info.referencedTextureNodes) {
                        this.releaseTextureResource(textureNode)
                    }
                    for (const tiledTextureInstance of cachedMaterial.info.referencedTiledTextureInstances) {
                        tiledTextureInstance.release()
                    }
                    material.dispose()
                    for (const variation of cachedMaterial.variations) variation.dispose()
                    this.logger.info("Material disposed", cachedMaterial.key)
                }
            } else material.dispose()
        }
    }

    ngOnDestroy() {
        this.clearTextures()
    }

    private clearTextures() {
        for (const cachedMaterial of this.materialCache.values()) {
            const {material, info} = cachedMaterial
            for (const textureNode of info.referencedTextureNodes) {
                this.releaseTextureResource(textureNode)
            }
            material.dispose()
            for (const variation of cachedMaterial.variations) variation.dispose()
            this.logger.info("Material disposed", material)
        }

        this.keyByThreeMaterial.clear()
        this.materialCache.clear()
        this.textureCache.clear()
    }

    releaseTextureResource(textureNode: THREE.Texture | THREE.TextureNode | THREE.RenderTarget<THREE.Texture | THREE.Texture[]>) {
        if (textureNode instanceof THREE.Texture) {
            if (!this.textureCache.release(textureNode)) textureNode.dispose()
        } else if (textureNode instanceof THREE.RenderTarget) {
            textureNode.dispose()
        } else {
            if (!this.textureCache.release(textureNode.value)) textureNode.value.dispose()
        }
    }

    updateMaterials(updateFunction: (material: THREE.Material) => void) {
        for (const cachedMaterial of this.materialCache.values()) {
            updateFunction(cachedMaterial.material)
            for (const variation of cachedMaterial.variations) updateFunction(variation)
        }
    }

    acquireVariation<M extends THREE.Material>(material: M, matcher: (material: THREE.Material) => material is M): M {
        const cachedMaterial = [...this.materialCache.values()].find((x) => x.material === material || x.variations.includes(material))
        if (cachedMaterial === undefined) throw new Error("Material not found")

        const {material: originalMaterial, variations} = cachedMaterial

        const variation = [originalMaterial, ...variations].find(matcher)
        if (variation) {
            cachedMaterial.refCount++
            return variation
        } else {
            const variation = material.clone()
            this.materialModifier?.(variation)
            cachedMaterial.variations.push(variation)
            this.keyByThreeMaterial.set(variation, cachedMaterial.key)

            cachedMaterial.refCount++
            return variation
        }
    }

    getMaxTextureSize(renderer: THREE.WebGPURenderer | undefined): number | undefined {
        if (!renderer) return undefined
        const backend = renderer.backend as {
            isWebGPUBackend?: true
            isWebGLBackend?: true
            device?: {limits?: {maxTextureDimension2D?: number}}
            gl?: WebGL2RenderingContext
        }
        if (backend.isWebGPUBackend) {
            return backend.device?.limits?.maxTextureDimension2D
        }
        if (backend.isWebGLBackend && backend.gl) {
            return backend.gl.getParameter(backend.gl.MAX_TEXTURE_SIZE) as number
        }
        return undefined
    }

    loadTexture(
        texture: DataObject | TypedImageData,
        critical: boolean,
        colorSpace: THREE.ColorSpace,
        minFilter: THREE.MinificationTextureFilter,
        magFilter: THREE.MagnificationTextureFilter,
        wrapping: THREE.Wrapping,
    ): Promise<THREE.Texture> {
        const computeResizeOptions = (sourceWidth: number | null | undefined, sourceHeight: number | null | undefined, sourceLabel: string) => {
            if (!this.maxTextureSize || !sourceWidth || !sourceHeight) return undefined
            if (sourceWidth <= this.maxTextureSize && sourceHeight <= this.maxTextureSize) return undefined
            const scale = Math.min(this.maxTextureSize / sourceWidth, this.maxTextureSize / sourceHeight)
            const resizeWidth = Math.max(1, Math.floor(sourceWidth * scale))
            const resizeHeight = Math.max(1, Math.floor(sourceHeight * scale))
            this.logger.warn(
                `Texture ${sourceLabel} (${sourceWidth}x${sourceHeight}) exceeds max texture size ${this.maxTextureSize}, rescaling to ${resizeWidth}x${resizeHeight}`,
            )
            return {resizeWidth, resizeHeight, resizeQuality: "high" as const}
        }

        const loadDataObjectBuffer = async (dataObject: DataObject): Promise<Uint8Array> => {
            const response = await fetchOk(dataObject.downloadUrl)
            const arrayBuffer = await response.arrayBuffer()
            return new Uint8Array(arrayBuffer)
        }

        const setUpTexture = (texture: THREE.Texture) => {
            texture.colorSpace = colorSpace
            texture.minFilter = minFilter
            texture.magFilter = magFilter
            texture.wrapS = texture.wrapT = wrapping
            texture.anisotropy = 1
            return texture
        }

        const {loadTexturePromise, description} = (() => {
            if (!isTypedImageData(texture)) {
                const {downloadUrl, mediaType, width, height} = texture
                const extension = extensionForContentType(mediaType)

                const canUseBitmapPath = extension !== "exr"
                const loadTexturePromise = canUseBitmapPath
                    ? async () => {
                          const buffer = await loadDataObjectBuffer(texture)

                          const {bitmapSource, sourceWidth, sourceHeight} = ((): {
                              bitmapSource: ImageBitmapSource
                              sourceWidth: number | null | undefined
                              sourceHeight: number | null | undefined
                          } => {
                              if (extension === "tif") {
                                  const imageData = decodeTiffToImageData(buffer)
                                  return {bitmapSource: imageData, sourceWidth: imageData.width, sourceHeight: imageData.height}
                              }
                              return {
                                  bitmapSource: new Blob([buffer as Uint8Array<ArrayBuffer>], {type: mediaType}),
                                  sourceWidth: width,
                                  sourceHeight: height,
                              }
                          })()

                          const resizeOptions = computeResizeOptions(sourceWidth, sourceHeight, downloadUrl)
                          const bitmap = await createImageBitmap(bitmapSource, {...resizeOptions, imageOrientation: "from-image"})

                          if (this.maxTextureSize && (bitmap.width > this.maxTextureSize || bitmap.height > this.maxTextureSize)) {
                              this.logger.warn(
                                  `Loaded texture ${downloadUrl} (${bitmap.width}x${bitmap.height}) still exceeds max texture size ${this.maxTextureSize} — GPU upload may fail`,
                              )
                          }

                          const threeTexture = new THREE.Texture(bitmap)
                          threeTexture.needsUpdate = true

                          return setUpTexture(threeTexture)
                      }
                    : undefined

                const rawLoadTexturePromise =
                    loadTexturePromise ??
                    (async () => {
                        if (this.maxTextureSize && width && height && (width > this.maxTextureSize || height > this.maxTextureSize)) {
                            this.logger.warn(
                                `Texture ${downloadUrl} (${width}x${height}) exceeds max texture size ${this.maxTextureSize} — cannot be rescaled, GPU upload may fail`,
                            )
                        }

                        return new Promise<THREE.Texture>((resolve, reject) => {
                            const loader =
                                extension === "exr" ? new EXRLoader().setDataType(DEFAULT_FLOAT_TEXTURE_TYPE ?? THREE.HalfFloatType) : new THREE.TextureLoader()

                            loader.load(
                                downloadUrl,
                                (texture) => {
                                    resolve(setUpTexture(texture))
                                },
                                undefined,
                                () => {
                                    reject(new Error(`Failed to load texture from url: ${downloadUrl} with media type: ${mediaType}`))
                                },
                            )
                        })
                    })
                const cacheKey = `${downloadUrl}|${colorSpace}|${minFilter}|${magFilter}|${wrapping}`
                return {
                    loadTexturePromise: () => this.textureCache.acquire(cacheKey, downloadUrl, rawLoadTexturePromise),
                    description: `loadTexture(${downloadUrl}, mediaType: ${mediaType})`,
                }
            } else {
                return {
                    loadTexturePromise: async () => {
                        if (this.maxTextureSize && (texture.width > this.maxTextureSize || texture.height > this.maxTextureSize)) {
                            this.logger.warn(
                                `In-memory texture (${texture.width}x${texture.height}) exceeds max texture size ${this.maxTextureSize} — cannot be rescaled, GPU upload may fail`,
                            )
                        }

                        const needsHalfFloatConversion = texture.dataType === "float32" && DEFAULT_FLOAT_TEXTURE_TYPE === THREE.HalfFloatType
                        const bufferSource = (() => {
                            if (!(texture.data.buffer instanceof ArrayBuffer)) throw Error("DecodedInMemoryImageData data is expected to be an ArrayBuffer")
                            if (needsHalfFloatConversion) {
                                const src = new Float32Array(texture.data.buffer)
                                const dst = new Uint16Array(src.length)
                                for (let i = 0; i < src.length; i++) dst[i] = THREE.DataUtils.toHalfFloat(src[i])
                                return dst
                            }
                            return new (getTypedArrayConstructorByDataType(texture.dataType))(texture.data.buffer)
                        })()
                        const format = (() => {
                            if (texture.channelLayout === "RGBA") return THREE.RGBAFormat
                            else if (texture.channelLayout === "L") return THREE.RedFormat
                            throw Error(`Unsupported channel layout: ${texture.channelLayout}`)
                        })()
                        const dataType = (() => {
                            if (needsHalfFloatConversion) return THREE.HalfFloatType
                            if (texture.dataType === "uint8") return THREE.UnsignedByteType
                            else if (texture.dataType === "uint16") return THREE.UnsignedShortType
                            else if (texture.dataType === "float16") return THREE.HalfFloatType
                            else if (texture.dataType === "float32") return THREE.FloatType
                            throw Error(`Unsupported data type: ${texture.dataType}`)
                        })()
                        const threeTexture = new THREE.DataTexture(bufferSource, texture.width, texture.height, format, dataType)
                        threeTexture.flipY = true
                        setUpTexture(threeTexture)
                        threeTexture.needsUpdate = true
                        return threeTexture
                    },
                    description: `loadTexture(DecodedInMemoryImageData)`,
                }
            }
        })()

        return new Promise<THREE.Texture>((resolve, reject) => {
            const observable = defer(() => loadTexturePromise()).pipe(
                tap((x) => {
                    resolve(x)
                }),
                catchError((error) => {
                    reject(error)
                    return EMPTY
                }),
            )

            if (this.threeSceneManagerService === undefined) throw new Error("ThreeSceneManagerService is not set")

            this.threeSceneManagerService.sceneManagerService.addTask({
                description,
                task: observable,
                critical,
            })
        })
    }
}

export const getDefaultMaterial = (materialIndex: number) => {
    const {color, roughness, metalness} = defaultMaterialParameters(materialIndex)
    const material = new ThreeColormassMaterial({
        colorNode: TSL.color(color[0], color[1], color[2]),
        roughnessNode: TSL.float(roughness),
        metalnessNode: TSL.float(metalness),
    })

    return material
}

export type MaterialInfo = {
    isTransparent: boolean
    referencedTextureNodes: (THREE.Texture | THREE.TextureNode | THREE.RenderTarget<THREE.Texture | THREE.Texture[]>)[]
    referencedTiledTextureInstances: ThreeTiledTextureInstance[]
    imageDataObjects: ImageDataObjectInfo[]
    defaultMaterial: boolean
    textureStreamingMode: TextureStreamingMode
}
